import { memo } from "react";
import { Trash2 } from "lucide-react";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { Button } from "@/shared/components/ui/button";
import { isFullyAvailable } from "../../utils/stock-availability.utils";
import { StockAvailabilityAlert } from "./StockAvailabilityAlert";

interface SelectedStocksListProps {
  items: StockAvailabilityResponseDTO[];
  onRemove: (stockId: number) => void;
}

/**
 * Lista as peças adicionadas ao aluguel (apenas tab SUIT).
 *
 * Mantém o alerta de indisponibilidade visível enquanto a peça
 * permanecer no aluguel — em modo compact ao lado do barcode e
 * detalhado por dia abaixo do card. Quando a lista está vazia,
 * mostra mensagem instruindo que o aluguel pode ser salvo incompleto.
 */
export const SelectedStocksList = memo(function SelectedStocksList({
  items,
  onRemove,
}: SelectedStocksListProps) {
  if (items.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Nenhuma peça adicionada. Você pode salvar o aluguel sem peças e
        completá-lo depois.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Peças selecionadas ({items.length})
      </p>
      <ul className="space-y-2">
        {items.map((item) => {
          const available = isFullyAvailable(item.statusByDate);
          return (
            <li
              key={item.stockId}
              className="rounded-md border border-input bg-background p-3 space-y-2"
            >
              <div className="flex items-center gap-3">
                {item.photoUrl ? (
                  <img
                    src={item.photoUrl}
                    alt=""
                    className="w-12 h-12 rounded object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-muted shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.barcode}</p>
                  {!available ? (
                    <StockAvailabilityAlert
                      statusByDate={item.statusByDate}
                      compact
                    />
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onRemove(item.stockId)}
                  aria-label={`Remover peça ${item.barcode}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {!available ? (
                <StockAvailabilityAlert statusByDate={item.statusByDate} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
});
